/**
 * Health Empty State - Shows helpful guidance when dashboard has no data
 */

import React from 'react';
import { Button } from './ui';
import {
  ChartBarIcon,
  LinkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface HealthEmptyStateProps {
  ouraConnected: boolean;
  appleHealthConnected: boolean;
  onConnectOura: () => void;
  onSyncOura?: () => void;
  ouraLastSync?: string | null;
  appleHealthLastSync?: string | null;
}

const HealthEmptyState: React.FC<HealthEmptyStateProps> = ({
  ouraConnected,
  appleHealthConnected,
  onConnectOura,
  onSyncOura,
  ouraLastSync,
  appleHealthLastSync
}) => {
  // Determine the primary issue
  const noDataSources = !ouraConnected && !appleHealthConnected;
  const hasStaleData = (() => {
    if (!ouraConnected && !appleHealthConnected) return false;

    const now = Date.now();
    const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);

    const ouraStale = ouraConnected && ouraLastSync &&
      new Date(ouraLastSync).getTime() < sevenDaysAgo;

    const appleStale = appleHealthConnected && appleHealthLastSync &&
      new Date(appleHealthLastSync).getTime() < sevenDaysAgo;

    return ouraStale || appleStale;
  })();

  // Shared icon style for button icons
  const buttonIconStyle = { width: '20px', height: '20px' };

  return (
    <div style={{
      backgroundColor: COLORS.bg.panel,
      border: `1px solid ${COLORS.border.default}`,
      borderRadius: RADIUS,
      padding: SPACING[48],
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto',
    }}>
      {/* Icon */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: SPACING[24],
      }}>
        <div style={{
          backgroundColor: COLORS.bg.base,
          borderRadius: '50%',
          padding: SPACING[24],
          display: 'inline-flex',
        }}>
          <ChartBarIcon style={{
            width: '48px',
            height: '48px',
            color: COLORS.text.secondary,
          }} />
        </div>
      </div>

      {/* Title and Message */}
      {noDataSources ? (
        <>
          <h2 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            marginBottom: SPACING[12],
            marginTop: 0,
          }}>
            No Health Data Sources Connected
          </h2>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            marginBottom: SPACING[32],
            marginTop: 0,
            lineHeight: 1.6,
          }}>
            Connect your health devices to start tracking your metrics and see insights on this dashboard.
          </p>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING[12],
            alignItems: 'center',
          }}>
            <Button
              variant="primary"
              size="lg"
              onClick={onConnectOura}
              leftIcon={<LinkIcon style={buttonIconStyle} />}
            >
              Connect Oura Ring
            </Button>
          </div>

          {/* Help Text */}
          <div style={{
            marginTop: SPACING[32],
            padding: SPACING[16],
            backgroundColor: COLORS.bg.base,
            borderRadius: RADIUS,
            textAlign: 'left',
          }}>
            <h4 style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.primary,
              marginTop: 0,
              marginBottom: SPACING[8],
            }}>
              What can you track?
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: SPACING[24],
              fontSize: TYPOGRAPHY.body.sizes.small,
              color: COLORS.text.secondary,
              lineHeight: 1.8,
            }}>
              <li>Sleep quality and duration (Oura, Apple Health)</li>
              <li>Daily activity: steps, calories, exercise (Apple Health)</li>
              <li>Heart rate, HRV, readiness scores (Oura, Apple Health)</li>
              <li>Weight, body composition (Apple Health, manual)</li>
              <li>Custom metrics via manual entry</li>
            </ul>
          </div>
        </>
      ) : hasStaleData ? (
        <>
          <h2 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            marginBottom: SPACING[12],
            marginTop: 0,
          }}>
            Your Data Needs Updating
          </h2>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            marginBottom: SPACING[32],
            marginTop: 0,
            lineHeight: 1.6,
          }}>
            {ouraConnected && ouraLastSync && new Date(ouraLastSync).getTime() < Date.now() - (7 * 24 * 60 * 60 * 1000) && (
              <>Your Oura data hasn't synced in over a week. </>
            )}
            {appleHealthConnected && appleHealthLastSync && new Date(appleHealthLastSync).getTime() < Date.now() - (7 * 24 * 60 * 60 * 1000) && (
              <>Your Apple Health data is more than a week old. </>
            )}
            Sync now to see recent insights.
          </p>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING[12],
            alignItems: 'center',
          }}>
            {ouraConnected && onSyncOura && (
              <Button
                variant="primary"
                size="lg"
                onClick={onSyncOura}
                leftIcon={<ClockIcon style={buttonIconStyle} />}
              >
                Sync Oura Now
              </Button>
            )}
          </div>

          {/* Help Text */}
          <div style={{
            marginTop: SPACING[24],
            padding: SPACING[12],
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: `1px solid ${COLORS.status.warning}`,
            borderRadius: RADIUS,
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.primary,
          }}>
            <strong>Tip:</strong> Apple Health syncs automatically via Health Auto Export every few hours.
            Oura requires manual sync via the button above.
          </div>
        </>
      ) : (
        <>
          <h2 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            marginBottom: SPACING[12],
            marginTop: 0,
          }}>
            Waiting for Data
          </h2>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            marginBottom: SPACING[24],
            marginTop: 0,
            lineHeight: 1.6,
          }}>
            Your health devices are connected, but no data has been synced yet.
            {ouraConnected && ' Try syncing your Oura Ring now, or '}
            {appleHealthConnected && ' check your Apple Health Auto Export setup.'}
          </p>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING[12],
            alignItems: 'center',
          }}>
            {ouraConnected && onSyncOura && (
              <Button
                variant="primary"
                size="lg"
                onClick={onSyncOura}
                leftIcon={<ClockIcon style={buttonIconStyle} />}
              >
                Sync Oura Now
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HealthEmptyState;
